// TESTIFY /gradify API client (brief §13). Read-only endpoints only:
//   /validate → confirm screen, /export → the exam bundle to import.
//
// The signed /upload call is here (§13.8): the endpoint, method, body shape and
// validation order are confirmed live (2026-07-26). The one unconfirmed detail is
// the signature's canonical message, isolated in signing.ts — see uploadResult().
import type { TestifyValidateResponse, TestifyExportResponse, TestifyUploadBody, TestifyUploadResponse } from './types.js';

// Default to a same-origin path that the Vite dev/preview server and the Docker
// nginx both proxy to the real API (vite.config.ts → TESTIFY_PROXY,
// deploy/nginx.conf). Override with VITE_TESTIFY_BASE to call the API directly.
//
// The CORS history matters, because that default is only correct where a proxy
// exists. On 2026-07-26 the API sent no CORS headers and its OPTIONS preflight
// 404d, so a direct cross-origin fetch was impossible and the proxy was the only
// option. Re-verified 2026-08-24: the API now answers the preflight 204 with
// access-control-allow-origin reflecting the request origin (any origin, incl.
// the https://localhost that Capacitor serves the Android build from), so a
// direct call now works. Any build with nothing proxying /gradify-api/ —
// the Android APK, or dist/ on a plain static host — MUST set
// VITE_TESTIFY_BASE, or every request lands on the app's own server and 404s.
// readBody() below reports that case as a missing API, not a bad exam code.
// `import.meta.env` exists under Vite; guard it so the module also loads in a
// bare Node/tsx test harness (where import.meta.env is undefined).
const RAW_BASE = ((import.meta as unknown as { env?: Record<string, string> }).env?.VITE_TESTIFY_BASE as string | undefined) || '/gradify-api/';
export const TESTIFY_BASE = RAW_BASE.endsWith('/') ? RAW_BASE : RAW_BASE + '/';

/** Resolve an endpoint against the base, which may be relative (proxy) or absolute. */
function apiUrl(path: string): string {
  // In the browser a relative base resolves against the page origin; in a bare
  // Node test harness there is no `location`, so fall back to a dummy origin
  // (the request is stubbed there and the host is irrelevant).
  const origin = typeof location !== 'undefined' ? location.origin : 'http://localhost';
  const base = /^https?:/i.test(TESTIFY_BASE) ? TESTIFY_BASE : new URL(TESTIFY_BASE, origin).toString();
  return new URL(path, base).toString();
}

export class TestifyApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * How long each call may take before it is abandoned. Without a deadline a
 * stalled connection — a captive portal, a proxy whose upstream never answers,
 * a phone that dropped off Wi-Fi mid-request — leaves fetch pending forever and
 * the teacher watching "Validating…" with no error and nothing to retry.
 */
export const TESTIFY_TIMEOUTS = {
  /** small JSON handshake. */
  validate: 20_000,
  /** the whole exam bundle: a hundred subjects is ~1 MB, slow on mobile data. */
  export: 120_000,
  /** one paper's responses, including the server-side enqueue. */
  upload: 45_000,
} as const;

interface RawResponse {
  status: number;
  ok: boolean;
  text: string;
  /**
   * The API only ever speaks JSON, so an HTML body means the request never
   * reached TESTIFY — it was answered by whatever is serving the app (a static
   * host with no /gradify-api/ proxy 404s that path; one with an SPA fallback
   * returns the app shell with a 200). Reporting either as "exam code not found"
   * sends a teacher off to re-check credentials that were never the problem.
   */
  html: boolean;
}

/**
 * One request under one deadline, body included. The body read is deliberately
 * inside the timeout: a response whose headers arrive and whose body then stalls
 * would otherwise hang just as badly as one that never answers at all.
 */
async function request(url: string, init: RequestInit, ms: number, what: string): Promise<RawResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await fetch(url, { ...init, signal: controller.signal });
    const text = await res.text();
    return { status: res.status, ok: res.ok, text, html: /^\s*(?:<!doctype|<html|<)/i.test(text) };
  } catch (e) {
    // Abort is reported by the signal on modern engines and by the error name on
    // older WebViews; either one means we gave up, not that the network refused.
    if (controller.signal.aborted || (e as { name?: string } | null)?.name === 'AbortError') {
      throw new TestifyApiError(
        0,
        `${what} timed out after ${Math.round(ms / 1000)}s. The network or the TESTIFY server never answered — check your connection and try again.`,
      );
    }
    throw new TestifyApiError(0, 'Network error — check your connection and try again.');
  } finally {
    clearTimeout(timer);
  }
}

/** We reached a web server, but not the API. Name the cause, not a fake symptom. */
function notTheApi(status: number): TestifyApiError {
  return new TestifyApiError(
    status,
    `No TESTIFY API at ${TESTIFY_BASE} — got a web page instead of a response (HTTP ${status}). ` +
      'Serve the app through the same-origin /gradify-api/ proxy: npm run dev, npm run preview, ' +
      'or the Docker image (deploy/nginx.conf). A plain static host has no proxy — build it with ' +
      'VITE_TESTIFY_BASE set to the API URL instead.',
  );
}

// Both endpoints are POST with a JSON body { exam_code } and a Bearer token
// (confirmed against the live API 2026-07-26), returning { ok: true, ... }.
async function postJson<T extends { ok?: boolean }>(
  path: string,
  examCode: string,
  token: string,
  ms: number,
  what: string,
): Promise<T> {
  const res = await request(
    apiUrl(path),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ exam_code: examCode }),
    },
    ms,
    what,
  );
  // Checked BEFORE the status mapping: an HTML 404 is a missing proxy, not a
  // missing exam, and an HTML 200 is the SPA shell swallowing the API path.
  if (res.html) throw notTheApi(res.status);
  if (res.status === 401) throw new TestifyApiError(401, 'Invalid or missing token.');
  if (res.status === 403) throw new TestifyApiError(403, 'Paper mode not enabled for this exam, or school mismatch.');
  if (res.status === 404) throw new TestifyApiError(404, 'Exam code not found for this token.');
  if (!res.ok) throw new TestifyApiError(res.status, `TESTIFY request failed (${res.status}).`);
  let data: T;
  try {
    data = JSON.parse(res.text) as T;
  } catch {
    throw new TestifyApiError(res.status, 'TESTIFY sent a reply GRADIFY could not read (not JSON).');
  }
  if (data && data.ok === false) throw new TestifyApiError(res.status, 'TESTIFY rejected the request.');
  return data;
}

/** Handshake: resolve the token's school + subject list for the confirm screen. */
export function validateExam(examCode: string, token: string): Promise<TestifyValidateResponse> {
  return postJson<TestifyValidateResponse>('validate', examCode, token, TESTIFY_TIMEOUTS.validate, 'Validating the exam');
}

/** Pull the full exam bundle (subjects, questions, hashed keys, signing key). */
export function exportExam(examCode: string, token: string): Promise<TestifyExportResponse> {
  return postJson<TestifyExportResponse>('export', examCode, token, TESTIFY_TIMEOUTS.export, 'Fetching the exam');
}

/**
 * Upload one student's scanned responses (one or more subjects) to TESTIFY, which
 * enqueues them for server-side scoring. The body carries per-subject + file HMAC
 * signatures (built in signing.ts). Distinct errors so the outbox can decide retry
 * vs. park (confirmed codes, §13.8): 401 token · 403 exam/school mismatch · 409
 * stale compile_version (re-import) · 422 bad LRN / empty items / tamper.
 */
export async function uploadResult(token: string, body: TestifyUploadBody): Promise<TestifyUploadResponse> {
  const res = await request(
    apiUrl('upload'),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    },
    TESTIFY_TIMEOUTS.upload,
    'The upload',
  );
  // Same ambiguity as postJson: without this a missing proxy reads as a plain
  // "upload failed (404)" and the outbox retries a request that can never land.
  if (res.html) throw notTheApi(res.status);
  let data: TestifyUploadResponse | null = null;
  try {
    data = JSON.parse(res.text) as TestifyUploadResponse;
  } catch {
    /* non-JSON error body */
  }
  if (res.ok && data?.ok) return data;
  const msg = data?.error ?? `TESTIFY upload failed (${res.status}).`;
  throw new TestifyApiError(res.status, msg);
}
